/** @format */

import { Auth } from './Auth';
import { history } from '../../core/history';
import { apiCall } from '../../core/apiCall';
import { actionDebug } from '../../core/debug';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { DateHelper } from '../../helpers/DateHelper';
import moment from 'moment';
import { Dispatch } from 'redux';
import PlatformUser from '../../interfaces/platform/PlatformUser';

const axios = require('axios');
const _ = require('underscore');

declare global {
    interface Window {
        _ROOT_LOGIN_PROVIDER: string;
    }
}

export const authActions = {
    LOGIN: 'login',
    LOGOK: 'logok',
    LOGERROR: 'logerror',
    LOGOUT: 'logout',
    ROLES: 'roles',
    CHANGE_PASSWORD: 'auth_change_password',
    TOGGLE_MODAL: 'auth_toggle_modal',
};

export const certificationActions = {
    CERTIFICATION_REQUEST: 'certification_request_list',
    CERTIFICATION_REQUEST_POST: 'certification_request_post',
};

export const authApiActions = {
    login: (username: string, password: string): any => {
        const request = (username: string): { type: string; username: string } => {
            return { type: authActions.LOGIN, username };
        };

        const failure = (message: any): { type: string; message: string; loginErrorMessage?: string | null } => {
            let msg = message;
            if (message.message) {
                msg = message.message;
            }
            return { type: authActions.LOGERROR, message: msg, loginErrorMessage: msg };
        };

        return (dispatch: Dispatch): void => {
            dispatch(request(username));
            handleLogin(username, password)
                .then((token: string) => {
                    Auth.storeToken(token);
                    if (Auth.userHasActiveSerial()) {
                        handlePing()
                            .then(() => {
                                handleCheckPlatformUser()
                                    .then((response: { data: PlatformUser }) => {
                                        handlePlatformUserSuccess(response, dispatch);
                                    })
                                    .catch((error: Error) => {
                                        if (error.message === 'system_account_cannot_login_to_the_application') {
                                            authApiActions.logout(dispatch);
                                            dispatch(failure(error.message));
                                            return;
                                        }
                                        history.push(process.env.REACT_APP_DEFAULT_PAGE ?? '');
                                    });
                            })
                            .catch((error: Error) => {
                                authApiActions.logout(dispatch);
                                dispatch(failure(error.message));
                            });
                    } else {
                        handleCheckPlatformUser()
                            .then((response: { data: PlatformUser }) => {
                                handlePlatformUserSuccess(response, dispatch);
                            })
                            .catch((error: Error) => {
                                if (error.message === 'system_account_cannot_login_to_the_application') {
                                    authApiActions.logout(dispatch);
                                    dispatch(failure(error.message));
                                    return;
                                }
                                history.push(process.env.REACT_APP_DEFAULT_PAGE ?? '');
                            });
                    }
                })
                .catch((error: Error) => {
                    dispatch(failure(error.message));
                });
        };
    },

    loginKerberos: (): any => {
        const failure = (message: any): { type: string; message: string; loginErrorMessage?: string | null } => {
            let msg = message;
            if (message.message) {
                msg = message.message;
            }
            return { type: authActions.LOGERROR, message: msg, loginErrorMessage: msg };
        };

        return (dispatch: Dispatch): void => {
            handleLoginKerberos()
                .then((token: string) => {
                    Auth.storeToken(token);
                    handleCheckPlatformUser()
                        .then((response: { data: PlatformUser }) => {
                            handlePlatformUserSuccess(response, dispatch);
                            history.push(process.env.REACT_APP_DEFAULT_PAGE ?? '');
                        })
                        .catch((error: Error) => {
                            if (error.message === 'system_account_cannot_login_to_the_application') {
                                authApiActions.logout(dispatch);
                                dispatch(failure(error.message));
                                return;
                            }
                            history.push(process.env.REACT_APP_DEFAULT_PAGE ?? '');
                        });
                })
                .catch((error: Error) => {
                    return;
                });
        };
    },

    logout: (dispatch: Dispatch | undefined): { type: string } => {
        Auth.clearToken();
        NotificationHelper.closeAllNotifications(dispatch);
        history.push(process.env.REACT_APP_LOGIN_PAGE ?? '');
        return { type: authActions.LOGOUT };
    },

    certificationLogout: (): { type: string } => {
        Auth.clearToken();
        history.push(process.env.REACT_APP_LOGIN_PAGE ?? '');
        return { type: authActions.LOGOUT };
    },
};

const handleLogin = (username: string, password: string): Promise<any> => {
    return axios
        .post(window._ROOT_LOGIN_PROVIDER, {
            username: username,
            password: password,
        })
        .then((response: { data: { token: string } }) => {
            actionDebug(response);
            const data = response.data;
            if (!_.has(data, 'token')) {
                return Promise.reject({ message: 'Empty token' });
            }
            return Promise.resolve(data.token);
        })
        .catch((error: Error) => {
            return apiCall.checkErrorResponse(error);
        });
};

const handleLoginKerberos = (): Promise<any> => {
    return axios
        .post(window._ROOT_LOGIN_PROVIDER, {})
        .then((response: { data: { token: string } }) => {
            actionDebug(response);
            const data = response.data;
            if (!_.has(data, 'token')) {
                return Promise.reject();
            }
            return Promise.resolve(data.token);
        })
        .catch((error: Error) => {
            return Promise.reject();
        });
};

const handlePing = (): Promise<any> => {
    const headers = apiCall.getHeaders();

    return axios
        .get(FormGeneralHelper.getUrl('/ping_platforms', 'platform'), { headers: headers })
        .then((response: { data: any }) => {
            // console.log({ response });
            return Promise.resolve(true);
        })
        .catch((error: Error) => {
            // console.error({ error });
            return Promise.reject(error);
        });
};

const handleCheckPlatformUser = (): Promise<any> => {
    const headers = apiCall.getHeaders();

    return axios
        .get(FormGeneralHelper.getUrl('/platform_users/' + Auth.getUserId(), 'platform'), { headers: headers })
        .then((response: { data: PlatformUser }) => {
            if (response.data.systemAccount) {
                return Promise.reject(new Error('system_account_cannot_login_to_the_application'));
            }
            // console.log({ response });
            return Promise.resolve(response);
        })
        .catch((error: Error) => {
            // console.error({ error });
            return Promise.reject(error);
        });
};

const handlePlatformUserPassword = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    if (!response || !response.data || response.data.alertFrequency === 0) {
        history.push(process.env.REACT_APP_DEFAULT_PAGE ?? '');
        return;
    }

    const { passwordExpiration, alertExpiration } = response.data;

    const passwordChanged = moment(response.data.passwordLastChanged);
    const expirationDate = moment();
    expirationDate.add(passwordExpiration);

    const passwordDateDifference = DateHelper.daysDifferent(passwordChanged, expirationDate);
    const passwordExpiresIn = (passwordDateDifference - (passwordExpiration ?? 0)) * -1;

    if (
        response.data.allowGenericLogin &&
        !response.data.singleLoginPassword &&
        passwordExpiresIn <= ((alertExpiration ?? 0) && passwordExpiresIn > -1)
    ) {
        if (passwordExpiresIn <= 1) {
            dispatch({
                type: authActions.TOGGLE_MODAL,
                passwordModalOpen: true,
                disableModalClose: true,
                modalModifiedSubTitle: 'auth:login_page.password_expiration_today_change_required',
            });
        }

        setTimeout(() => {
            NotificationHelper.notifyCustomPernament(
                passwordExpiresIn <= 1 ? 'auth:login_page.password_expiration_today' : 'auth:login_page.password_expiration_in',
                dispatch,
                'warning',
                false,
                {
                    replace: {
                        days: passwordExpiresIn,
                    },
                }
            );
        }, 1000);
    }
    history.push(process.env.REACT_APP_DEFAULT_PAGE ?? '');
};

const handlePlatformUserCertificate = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    const { certificateValidTo } = response.data;
    if (typeof certificateValidTo === 'undefined' || certificateValidTo === null) {
        return;
    }

    const validTo = moment(response.data.certificateValidTo);
    const expirationDate = moment();

    const dateDifference = DateHelper.daysDifferent(validTo, expirationDate) * -1;

    if (dateDifference <= 3) {
        setTimeout(() => {
            NotificationHelper.notifyCustomPernament(
                dateDifference <= 1 ? 'auth:login_page.certificate_expiration_today' : 'auth:login_page.certificate_expiration_in',
                dispatch,
                'warning',
                false,
                {
                    replace: {
                        days: dateDifference,
                    },
                }
            );
        }, 1000);
    }
};

const handlePlatformUserSystemAccessPropagation = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    if (!response.data.systemAccess) {
        return;
    }

    Auth.storeSystemAccess(response.data.systemAccess);
};

const handlePlatformUserRolePropagation = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    if (!response.data.platformUserApiRoles) {
        return;
    }

    Auth.storeRoles(response.data.platformUserApiRoles);
};

const handlePlatformUserPasswordStatePropagation = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    if (!response.data.singleLoginPassword === undefined) {
        return;
    }

    Auth.storePasswordState({ isSingleUse: response.data.singleLoginPassword || false });
};

const handlePlatformUserCertificationPropagation = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    if (!response.data.allowGenericLogin === undefined) {
        return;
    }

    Auth.storeCertificationState({ allowGenericLogin: response.data.allowGenericLogin || false });
};

const handlePlatformUserCustomerLevel = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    if (!response.data.customerLevel) {
        return;
    }
    Auth.storeCustomerLevel(response.data.customerLevel);
};

const handlePlatformUserSuccess = (response: { data: PlatformUser }, dispatch: Dispatch): void => {
    handlePlatformUserSystemAccessPropagation(response, dispatch);
    handlePlatformUserRolePropagation(response, dispatch);
    handlePlatformUserPasswordStatePropagation(response, dispatch);
    handlePlatformUserCertificationPropagation(response, dispatch);
    handlePlatformUserCertificate(response, dispatch);
    handlePlatformUserPassword(response, dispatch);
    handlePlatformUserCustomerLevel(response, dispatch);
};
